import { LOCALES, isLocale, negotiateLocale, type Locale } from '@bitwize/i18n-messages';

/**
 * Locale-prefixed URL handling.
 *
 * Every public URL carries its locale: `/en/projects`, `/ar/projects`. This is
 * not cosmetic — it is what lets each locale be crawled, linked, cached and
 * given an `hreflang` independently (docs/seo.md §2).
 */

/**
 * The locale an unprefixed visitor lands on when nothing better is known.
 *
 * **English** (Q-07, resolved). It is the last resort in the chain, not the
 * first: a visitor who has chosen a language, or whose browser asks for Arabic,
 * gets Arabic. English is what an unknown visitor sees.
 *
 * Still read from configuration, because "default locale" is the kind of thing
 * a second deployment of this codebase would want to change without a code
 * change.
 */
export const DEFAULT_LOCALE: Locale = readDefaultLocale();

function readDefaultLocale(): Locale {
  // Two sources, because this module is loaded both by Vite (browser and SSR
  // bundles, where `import.meta.env` is substituted at build time) and by plain
  // Node in the server entry (where it does not exist at all).
  const fromBundle = import.meta.env?.PUBLIC_ENV__DEFAULT_LOCALE;
  if (isLocale(fromBundle)) return fromBundle;

  const fromProcess = typeof process === 'undefined' ? undefined : process.env['DEFAULT_LOCALE'];
  if (isLocale(fromProcess)) return fromProcess;

  return 'en';
}

export interface ParsedPath {
  locale: Locale | null;
  /** The path with the locale segment removed, always starting with `/`. */
  pathname: string;
}

/** Split a locale prefix off a pathname. `/ar/projects` → `{ ar, /projects }`. */
export function parseLocalePath(rawPath: string): ParsedPath {
  const [, first = '', ...rest] = rawPath.split('/');

  if (!isLocale(first)) {
    return { locale: null, pathname: rawPath === '' ? '/' : rawPath };
  }

  const remainder = rest.join('/');
  return { locale: first, pathname: remainder === '' ? '/' : `/${remainder}` };
}

/**
 * The pathname of a raw URL, query string and hash stripped.
 *
 * Vike's own `pageContext.urlPathname` is a getter it installs server-side
 * (`onRenderHtml`) but never installs on the client under server routing
 * (this project's mode — `runtime-client-routing` is the only place Vike
 * builds that getter). Reading `pageContext.urlPathname` from a component
 * that renders on both sides therefore works during SSR and throws on
 * hydration. `pageContext.urlOriginal`, a plain string, is the one URL field
 * Vike actually passes to the client, so it — not `urlPathname` — is what an
 * isomorphic component derives a pathname from.
 */
export function pathnameOf(url: string): string {
  return url.split('?')[0]?.split('#')[0] ?? '/';
}

/** Build a locale-prefixed URL. `('ar', '/projects')` → `/ar/projects`. */
export function localePath(locale: Locale, pathname = '/'): string {
  const normalised = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return normalised === '/' ? `/${locale}` : `/${locale}${normalised}`;
}

/**
 * The equivalent URL in another locale, preserving the query string and hash.
 *
 * Preservation matters: switching language from a filtered portfolio listing
 * must keep the filters, and switching from a project detail page must land on
 * the same project rather than the home page.
 *
 * Per-locale slugs mean the path segment itself may differ. That mapping comes
 * from the API's `localized_slugs` on a detail page (see `AlternatePaths`
 * below and each detail page's `+data.ts`) — when the caller has one for the
 * target locale, `alternatePath` carries it; otherwise the current path is
 * reused, which is correct for every route whose path does not depend on a
 * slug at all.
 */
export function switchLocaleUrl(
  currentUrl: string,
  target: Locale,
  alternatePath?: string,
): string {
  const [pathWithQuery = '/', hash] = currentUrl.split('#');
  const [path = '/', query] = pathWithQuery.split('?');

  const { pathname } = parseLocalePath(path);
  const nextPath = localePath(target, alternatePath ?? pathname);

  return nextPath + (query ? `?${query}` : '') + (hash ? `#${hash}` : '');
}

/**
 * A detail page's real path in each locale it exists in, keyed by locale —
 * bare pathnames with no locale prefix, exactly the shape `switchLocaleUrl`'s
 * `alternatePath` parameter expects.
 *
 * Absent entirely on any page whose path does not depend on a slug (every
 * listing, FAQ, contact, home) — the language switcher's default
 * same-path behaviour already handles those correctly, and this only needs
 * to exist where it changes that behaviour.
 */
export type AlternatePaths = Partial<Record<Locale, string>>;

/**
 * Builds an `AlternatePaths` map from a detail entity's `localized_slugs` —
 * the API field carrying that entity's slug in every locale it has one.
 *
 * A single shared function rather than one written per entity type: every
 * detail page (project, service, technology, team member) follows the exact
 * same rule — `{routePrefix}/{that locale's slug}` — and the language
 * switcher must never depend on a route knowing this; the route computes the
 * map once, here, and hands it a finished answer.
 */
export function alternatePathsFrom(
  routePrefix: string,
  localizedSlugs: Record<string, string> | undefined,
): AlternatePaths {
  const alternates: AlternatePaths = {};
  for (const [rawLocale, slug] of Object.entries(localizedSlugs ?? {})) {
    if (isLocale(rawLocale) && slug) alternates[rawLocale] = `${routePrefix}/${slug}`;
  }
  return alternates;
}

export interface LocaleRequestHints {
  /** The `Accept-Language` request header, if any. */
  readonly acceptLanguage?: string | null | undefined;
  /**
   * The locale this visitor last read the site in, recovered from the
   * preference cookie. See `preference.ts` for why a cookie exists at all.
   */
  readonly savedLocale?: Locale | null | undefined;
}

/**
 * Resolve the locale for an incoming request.
 *
 * The chain, most specific first:
 *
 *   1. **An explicit prefix in the URL.** A link someone sent, a bookmark, a
 *      search result. It is unconditional — a stored preference must never
 *      override the page a visitor actually asked for.
 *   2. **The saved preference.** A returning visitor who chose Arabic once
 *      should not have to choose again.
 *   3. **`Accept-Language`.** A first-time visitor whose browser says Arabic
 *      gets Arabic without doing anything.
 *   4. **`DEFAULT_LOCALE`** — English (Q-07).
 *
 * `redirectTo` is set only when the URL carried no prefix, because a canonical,
 * cacheable, linkable URL always has one.
 */
export function resolveRequestLocale(
  url: string,
  hints: LocaleRequestHints = {},
): { locale: Locale; redirectTo: string | null } {
  const [pathWithQuery = '/', hash] = url.split('#');
  const [path = '/', query] = pathWithQuery.split('?');

  const { locale, pathname } = parseLocalePath(path);
  if (locale) {
    return { locale, redirectTo: null };
  }

  const resolved = hints.savedLocale ?? negotiateLocale(hints.acceptLanguage) ?? DEFAULT_LOCALE;
  const target =
    localePath(resolved, pathname) + (query ? `?${query}` : '') + (hash ? `#${hash}` : '');

  return { locale: resolved, redirectTo: target };
}

export { LOCALES, isLocale };
export type { Locale };
